import React from 'react';
import classNames from 'classnames/bind';
import styles from './selectItemComponent.module.scss';
import {
  SelectItemComponentState,
  SelectItemComponentProps,
  ItemType
} from './SelectItemComponentModel';
import i18n from 'i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
export class SelectItemComponent extends React.Component<
  SelectItemComponentProps,
  SelectItemComponentState
> {
  data: SelectOptions;
  hasOptions: boolean;
  cssClassNames: any;
  itemRef: { current: null | HTMLDivElement };
  handler?: number;

  constructor(props) {
    super(props);
    this.data = this.props.model.data;
    this.state = this.props.model.state;
    this.hasOptions = !!this.props.model.data.options;
    this.cssClassNames = classNames.bind(styles);
    this.itemRef = React.createRef();
  }

  componentDidMount() {
    this.handler = this.props.model.event.add((model) => {
      this.setState(model.state);
    });
    this.updateItemHeight();
  }

  componentDidUpdate(prevProps, prevStates) {
    if (prevProps.model !== this.props.model) {
      this.data = this.props.model.data;
      prevProps.model.event.remove(this.handler);
      this.handler = this.props.model.event.add((model) => {
        this.setState(model.state);
      });
      this.hasOptions = !!this.props.model.data.options;
      this.updateItemHeight();
    } else if (prevStates.isOpen !== this.state.isOpen) {
      this.updateItemHeight();
    }
  }

  componentWillUnmount() {
    this.handler && this.props.model.event.remove(this.handler);
  }

  updateItemHeight() {
    this.itemRef.current &&
      this.props.model.setItemHeight(this.itemRef.current.clientHeight);
  }

  selectValue = (data: SelectOptions) => {
    if (this.props.model.isSelected(data)) {
      return;
    }

    this.props.model.onSelect(data);
  };

  /**
   * 有「不能選的原因」時，把選項包一層 hover 提示（ECOPD-5615）。
   *
   * ⚠️ OverlayTrigger 不會產生包裹用的 DOM 元素，它是把 hover / focus handler 複製到
   * child 身上 —— 而 `.selectItem.disabled` 有 `pointer-events: none`，那些 handler
   * 會落在收不到指標事件的元素上。所以 scss 另外用 `.disabledWithReason` 把指標事件
   * 開回來（見 selectItemComponent.module.scss），只影響「有原因」的這一類。
   *
   * ⚠️ `container={document.body}`：這個清單是虛擬化的（react-window），外層 overflow
   * 會把 inline 渲染的 tooltip 裁掉一半。
   */
  withDisabledReason = (
    reason: string | undefined,
    key: string | number,
    content: JSX.Element
  ) =>
    reason ? (
      <OverlayTrigger
        key={key}
        placement='right'
        trigger={['hover', 'focus']}
        container={document.body}
        overlay={<Tooltip id={`optionDisabled-${key}`}>{reason}</Tooltip>}
      >
        {content}
      </OverlayTrigger>
    ) : (
      content
    );

  getOptionContent = (data: SelectOptions) => (
    <React.Fragment>
      {this.props.model.itemSetting.i18nPrefix
        ? i18n.t(
            `${this.props.model.itemSetting.i18nPrefix}.${data.value.toString().toLowerCase().replace(/-|\s/g, '_')}`
          )
        : data.label}
      {!this.props.model.itemSetting.hideExtra &&
      data.extra !== undefined &&
      data.extra !== null &&
      !Array.isArray(data.extra) ? (
        <div className={styles.extra}>
          {data.extra.i18nKey
            ? i18n.t(data.extra.i18nKey, { value: data.extra.value })
            : data.extra}
        </div>
      ) : undefined}
    </React.Fragment>
  );

  getOptions = () => {
    const model = this.props.model;
    if (!model.data.options) {
      return [];
    }

    const readonly = model.itemSetting.readonly;
    return model.data.options.map((option, index) => {
      const disabledReason = model.getOptionDisabledReason(option.value);
      const optionDisabled =
        model.disabled || model.isOptionDisabled(option.value);
      const optionClassName = this.cssClassNames('selectItem', {
        selected: model.isSelected(option),
        disabled: optionDisabled,
        // 有原因才要 hover 得到（見 scss 的說明）
        disabledWithReason: !!disabledReason,
        readonly: readonly
      });
      // 不只靠 .disabled 的 pointer-events: none —— 樣式擋不擋得住是別處的決定，
      // 「不能選」這件事要在行為上也成立（ECOPD-5615）。
      const clickFunc =
        readonly || optionDisabled ? undefined : () => this.selectValue(option);
      return this.withDisabledReason(
        disabledReason,
        option.value,
        <div className={optionClassName} key={index} onClick={clickFunc}>
          {this.getOptionContent(option)}
        </div>
      );
    });
  };

  render() {
    const model = this.props.model;
    const clickFunc = () => this.selectValue(model.data);
    if (
      model.itemSetting.type === ItemType.withCategory ||
      (model.data.options && model.data.options.length > 0) ||
      (model.itemSetting.type === ItemType.autoByGroupSetting &&
        model.data.isGroup)
    ) {
      const categorySelectable =
        model.itemSetting.categorySelectable === true &&
        !model.itemSetting.readonly;
      const itemClassName = this.cssClassNames('selectItemWithOptions', {
        open: this.state.isOpen
      });
      const categoryContainerClassName = this.cssClassNames(
        'categoryContainer',
        {
          selected: model.isSelected(model.data),
          disabled: model.disabled
        }
      );
      const categoryClassName = this.cssClassNames('category', {
        selectable: categorySelectable
      });

      return (
        <div className={itemClassName} ref={this.itemRef}>
          <div className={categoryContainerClassName}>
            <div
              className={styles.arrowContainer}
              onClick={this.props.model.toggleOpen}
            >
              <div className={styles.arrow} />
            </div>
            <div
              className={categoryClassName}
              onClick={categorySelectable ? clickFunc : undefined}
            >
              {this.getOptionContent(model.data)}
            </div>
          </div>
          <div className={styles.options}>{this.getOptions()}</div>
        </div>
      );
    } else {
      const readonly = model.itemSetting.readonly;
      // ECOPD-5615：攤平的一列本身就是選項（例如測試模式下的門市清單），
      // 所以這裡也要問 isOptionDisabled，否則只有樹狀的子選項會被灰掉。
      const dataDisabled = model.isOptionDisabled(model.data.value);
      const disabledReason = model.getOptionDisabledReason(model.data.value);
      const itemClassName = this.cssClassNames('selectItem', {
        selected: model.isSelected(model.data),
        disabled: model.disabled || dataDisabled,
        disabledWithReason: !!disabledReason,
        readonly: readonly
      });

      return this.withDisabledReason(
        disabledReason,
        model.data.value,
        <div
          className={itemClassName}
          onClick={readonly || dataDisabled ? undefined : clickFunc}
          ref={this.itemRef}
        >
          {this.getOptionContent(model.data)}
        </div>
      );
    }
  }
}
